import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Reveal from "@/components/base/Reveal";
import { useAuth } from "@/hooks/useAuth";
import { useHostDashboard } from "@/hooks/useHostDashboard";
import { useFlaggedBookings } from "@/hooks/useFlaggedBookings";
import { formatKES } from "@/lib/format";
import { isAwaitingPayment, isCancelled, tripPhase } from "@/lib/bookings";
import HostListingCard from "./components/HostListingCard";
import BookingRequestCard from "./components/BookingRequestCard";

type TabKey = "requests" | "listings";

export default function DashboardPage() {
  const { user, profile } = useAuth();
  const { listings, requests, loading, error, reload, updateBookingStatus, toggleListingAvailability } = useHostDashboard();
  const { flagged } = useFlaggedBookings();
  const [tab, setTab] = useState<TabKey>("requests");
  const [togglingId, setTogglingId] = useState<number | null>(null);

  const stats = useMemo(() => {
    const liveListings = listings.filter((vehicle) => vehicle.available !== false).length;
    const pending = requests.filter((request) => isAwaitingPayment(request.status) && !isCancelled(request.status)).length;
    const earnings = requests
      .filter((request) => ["confirmed", "ongoing", "completed"].includes(request.status || ""))
      .reduce((sum, request) => sum + Number(request.total_price ?? 0), 0);
    const upcoming = requests.filter((request) => tripPhase(request) === "upcoming" && !isCancelled(request.status)).length;
    return { liveListings, pending, earnings, upcoming };
  }, [listings, requests]);

  const pendingCount = stats.pending;

  const flaggedRequests = useMemo(
    () => requests.filter((request) => flagged.has(Number(request.id))),
    [requests, flagged]
  );

  async function handleToggle(vehicleId: number, available: boolean) {
    setTogglingId(vehicleId);
    try {
      await toggleListingAvailability(vehicleId, available);
    } finally {
      setTogglingId(null);
    }
  }

  return (
    <div className="min-h-screen w-full bg-background-50">
      <Navbar />

      <div className="mx-auto w-full max-w-6xl px-4 pb-20 pt-28 md:px-8 md:pt-36">
        {!user ? (
          <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-background-200 bg-background-100 px-6 py-20 text-center">
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200/60 text-3xl text-foreground-500">
              <i className="ri-dashboard-line"></i>
            </span>
            <h3 className="mt-5 font-heading text-lg font-semibold text-foreground-950">Sign in to your host dashboard</h3>
            <p className="mt-2 max-w-sm text-sm text-foreground-600">
              Manage your listings, accept booking requests and track earnings from here.
            </p>
            <Link
              to="/auth?redirect=/dashboard"
              className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-login-circle-line"></i> Sign in
            </Link>
          </div>
        ) : (
          <>
            <div className="flex flex-col gap-5 md:flex-row md:items-end md:justify-between">
              <div className="max-w-2xl">
                <span className="text-sm font-medium uppercase tracking-[0.2em] text-primary-500">Host dashboard</span>
                <h1 className="mt-3 font-heading text-3xl font-semibold text-foreground-950 md:text-4xl">
                  Karibu, {profile?.full_name?.split(" ")[0] || "host"}
                </h1>
                <p className="mt-3 text-base leading-relaxed text-foreground-600">
                  Keep an eye on your cars, respond to booking requests and watch your earnings grow.
                </p>
              </div>
              <Link
                to="/host"
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-add-circle-line"></i> List a car
              </Link>
            </div>

            {flaggedRequests.length > 0 && (
              <div className="mt-6 flex flex-col gap-4 rounded-2xl border border-secondary-500/40 bg-secondary-500/10 p-4 sm:flex-row sm:items-center sm:justify-between md:p-5">
                <div className="flex items-start gap-3">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-secondary-500 text-xl text-background-50">
                    <i className="ri-alert-line"></i>
                  </span>
                  <div>
                    <p className="font-heading text-sm font-semibold text-foreground-950">
                      {flaggedRequests.length} {flaggedRequests.length === 1 ? "trip has" : "trips have"} damage flagged
                    </p>
                    <p className="mt-1 text-sm text-foreground-600">
                      A rider flagged damage on {flaggedRequests.map((item) => `#${item.id}`).join(", ")}. Review the
                      before/after walkaround and dispute note before it gets buried.
                    </p>
                  </div>
                </div>
                <Link
                  to={`/booking/${flaggedRequests[0].id}`}
                  className="inline-flex shrink-0 items-center gap-2 whitespace-nowrap rounded-md bg-secondary-500 px-4 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-secondary-600"
                >
                  <i className="ri-scales-3-line"></i> Review dispute
                </Link>
              </div>
            )}

            <div className="mt-8 grid grid-cols-2 gap-4 lg:grid-cols-4">
              {[
                { label: "Live listings", value: stats.liveListings, icon: "ri-roadster-line" },
                { label: "Pending requests", value: pendingCount, icon: "ri-mail-unread-line" },
                { label: "Earnings", value: formatKES(stats.earnings), icon: "ri-wallet-3-line" },
                { label: "Upcoming trips", value: stats.upcoming, icon: "ri-calendar-event-line" },
              ].map((stat) => (
                <div key={stat.label} className="rounded-2xl surface-card p-5">
                  <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary-500/12 text-xl text-primary-400">
                    <i className={stat.icon}></i>
                  </span>
                  <p className="mt-3 font-heading text-2xl font-semibold text-foreground-950">{stat.value}</p>
                  <p className="mt-1 text-sm text-foreground-500">{stat.label}</p>
                </div>
              ))}
            </div>

            <div className="mt-8 inline-flex rounded-full bg-background-100 p-1">
              <button
                type="button"
                onClick={() => setTab("requests")}
                className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                  tab === "requests" ? "bg-primary-500 text-background-50" : "text-foreground-700 hover:text-foreground-950"
                }`}
              >
                <i className="ri-mail-open-line"></i> Booking requests
                {pendingCount > 0 && (
                  <span className={`rounded-full px-1.5 text-xs ${tab === "requests" ? "bg-background-50/25" : "bg-primary-500/15 text-primary-400"}`}>
                    {pendingCount}
                  </span>
                )}
              </button>
              <button
                type="button"
                onClick={() => setTab("listings")}
                className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                  tab === "listings" ? "bg-primary-500 text-background-50" : "text-foreground-700 hover:text-foreground-950"
                }`}
              >
                <i className="ri-roadster-line"></i> My listings
              </button>
            </div>

            <div className="mt-6">
              {loading && (
                <div className="space-y-4">
                  {Array.from({ length: 2 }).map((_, index) => (
                    <div key={index} className="h-40 w-full animate-pulse rounded-2xl bg-background-100" />
                  ))}
                </div>
              )}

              {!loading && error && (
                <div className="flex flex-col items-start gap-3 rounded-xl border border-secondary-700/40 bg-secondary-950/40 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
                  <p className="flex items-center gap-2 text-sm text-secondary-300">
                    <i className="ri-error-warning-line"></i> {error}
                  </p>
                  <button
                    type="button"
                    onClick={reload}
                    className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50"
                  >
                    <i className="ri-refresh-line"></i> Try again
                  </button>
                </div>
              )}

              {!loading && !error && tab === "requests" && (
                <>
                  {requests.length === 0 ? (
                    <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-background-200 bg-background-100 px-6 py-16 text-center">
                      <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200/60 text-3xl text-foreground-500">
                        <i className="ri-mail-open-line"></i>
                      </span>
                      <h3 className="mt-5 font-heading text-lg font-semibold text-foreground-950">No booking requests yet</h3>
                      <p className="mt-2 max-w-sm text-sm text-foreground-600">
                        When a rider books one of your cars, their request will appear here for you to accept or decline.
                      </p>
                      {listings.length === 0 && (
                        <Link
                          to="/host"
                          className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                        >
                          <i className="ri-add-circle-line"></i> List your first car
                        </Link>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {requests.map((request, index) => (
                        <Reveal key={request.id} delay={Math.min(index, 4) * 60}>
                          <BookingRequestCard request={request} onUpdate={updateBookingStatus} flagged={flagged.has(Number(request.id))} />
                        </Reveal>
                      ))}
                    </div>
                  )}
                </>
              )}

              {!loading && !error && tab === "listings" && (
                <>
                  {listings.length === 0 ? (
                    <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-background-200 bg-background-100 px-6 py-16 text-center">
                      <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200/60 text-3xl text-foreground-500">
                        <i className="ri-roadster-line"></i>
                      </span>
                      <h3 className="mt-5 font-heading text-lg font-semibold text-foreground-950">You haven&apos;t listed a car yet</h3>
                      <p className="mt-2 max-w-sm text-sm text-foreground-600">
                        Add your car to start earning — it takes just a few minutes.
                      </p>
                      <Link
                        to="/host"
                        className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                      >
                        <i className="ri-add-circle-line"></i> List a car
                      </Link>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                      {listings.map((vehicle, index) => (
                        <Reveal key={vehicle.id} delay={(index % 3) * 70}>
                          <HostListingCard
                            vehicle={vehicle}
                            bookingsCount={requests.filter((request) => request.vehicle_id === vehicle.id).length}
                            toggling={togglingId === vehicle.id}
                            onToggle={handleToggle}
                          />
                        </Reveal>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          </>
        )}
      </div>

      <Footer />
    </div>
  );
}